import {
  getMindboxSerpClickFields,
  type MindboxCustomFields,
  type MindboxSerpFilters,
  type MindboxSerpType,
} from '@/entities/analytics';
import type { SearchCriteria } from '@/entities/searchCriteria';
import {
  SERP_MINDBOX_AMENITIES,
  SERP_MINDBOX_BEACH_LINES,
  SERP_MINDBOX_BEACH_TYPES,
  SERP_MINDBOX_CHILD_SERVICES,
  SERP_MINDBOX_HOTEL_RATINGS,
  SERP_MINDBOX_WATER_ACTIVITIES,
  type SerpMindboxAmenity,
} from '../config/mindbox';
import type { FiltersModel, SerpProduct, SerpResultsDictionaries } from '../model/types';

export interface SerpMindboxHotelOpen {
  criteria: SearchCriteria;
  searchCriteria: SearchCriteria | null;
  filtersModel: FiltersModel;
  dictionaries: SerpResultsDictionaries;
}

function getSelectedValues<K extends string, V>(values: Readonly<Record<K, V>>, selected: readonly K[]): V[] {
  return (Object.keys(values) as K[]).filter((key) => selected.includes(key)).map((key) => values[key]);
}

function isAmenitySelected(amenity: SerpMindboxAmenity, filtersModel: FiltersModel): boolean {
  if ('flag' in amenity) return filtersModel[amenity.flag];
  if ('facility' in amenity) return filtersModel.facilities.includes(amenity.facility);

  return filtersModel.restType === amenity.restType;
}

function getAmenities(filtersModel: FiltersModel): string[] {
  return Object.values(SERP_MINDBOX_AMENITIES).flatMap((amenity) =>
    amenity && isAmenitySelected(amenity, filtersModel) ? [amenity.label] : [],
  );
}

function getBeachType([beachType]: FiltersModel['beachType']): string | null {
  return (beachType && SERP_MINDBOX_BEACH_TYPES[beachType]) ?? null;
}

function getTourOperators(selected: readonly string[], { operators }: SerpResultsDictionaries): string[] {
  const names = selected.flatMap((key) => operators.find((operator) => String(operator.id) === key)?.name ?? []);

  return [...new Set(names)];
}

export function isGroupSearch(criteria: SearchCriteria): boolean {
  return (criteria.tourists?.rooms.length ?? 0) > 1;
}

export function isGroupSerpProduct({ products }: SerpProduct): boolean {
  return products.some(({ roomGroups }) => roomGroups.length > 1);
}

export function getSerpMindboxFilters(
  filtersModel: FiltersModel,
  dictionaries: SerpResultsDictionaries,
): MindboxSerpFilters {
  return {
    beachType: getBeachType(filtersModel.beachType),
    amenities: getAmenities(filtersModel),
    budgetFrom: filtersModel.budget.min || null,
    budgetTo: filtersModel.budget.max || null,
    closeToSki: false,
    exclusive: filtersModel.isExclusive,
    hotelRating: filtersModel.rating ? SERP_MINDBOX_HOTEL_RATINGS[filtersModel.rating] : null,
    beachLines: getSelectedValues(SERP_MINDBOX_BEACH_LINES, filtersModel.beachLine),
    ownedBeach: filtersModel.beachOwner,
    waterActivities: filtersModel.waterActivities.map((key) => SERP_MINDBOX_WATER_ACTIVITIES[key]),
    childServices: getSelectedValues(SERP_MINDBOX_CHILD_SERVICES, filtersModel.childServices),
    tourOperators: getTourOperators(filtersModel.operators, dictionaries),
  };
}

export function getSerpMindboxClickFields(
  serpType: MindboxSerpType,
  { criteria, searchCriteria, filtersModel, dictionaries }: SerpMindboxHotelOpen,
): MindboxCustomFields {
  return getMindboxSerpClickFields(serpType, {
    criteria,
    searchCriteria: searchCriteria ?? criteria,
    filters: getSerpMindboxFilters(filtersModel, dictionaries),
  });
}
